/* Variant Picker — stable init (final v2)
   - DOMContentLoaded / shopify:section:load / shopify:section:select / pageshow 対応
   - MutationObserver で後挿入にも自動初期化
   - JSON.parse 保護
   - input[name="id"] が無い場合に警告
   - 二重初期化防止 data-vp-initialized="1"
   - 値の正規化（全角空白→半角 / trim）で表記ゆれに強く
   - data-option-index 欠落時のフォールバック（親グループ/NodeList順から算出）
   - IIFE内の委譲クリック（capture）で確実に updateUi を実行
*/
(() => {
  const PICKER_SELECTOR = ".c-variantPicker";

  // ===== util =====
  const parseJSON = (str, fallback) => {
    try { return JSON.parse(str); }
    catch (e) { console.warn("[variant-picker] JSON parse failed:", e); return fallback; }
  };
  const norm = (s) => (s == null ? "" : String(s)).replace(/\u3000/g, " ").trim(); // 全角空白→半角 + trim
  const arraysEqualNorm = (a, b) =>
    a && b && a.length === b.length && a.every((v, i) => norm(v) === norm(b[i]));

  const formatMoney = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === "function") return Shopify.formatMoney(cents);
    const n = typeof cents === "string" ? parseInt(cents.replace(".", ""), 10) : parseInt(cents, 10);
    return (n / 100).toLocaleString(undefined, { style: "currency", currency: (window.Shopify?.currency?.active) || "JPY" });
  };

  const getOptionIndex = (btn, wrapper) => {
    // 1) data-option-index
    let idx = Number.parseInt(btn?.dataset?.optionIndex, 10);
    if (Number.isFinite(idx)) return idx;
    // 2) 親グループの data-option-index
    const group = btn.closest(".c-variantPicker__options");
    idx = Number.parseInt(group?.dataset?.optionIndex, 10);
    if (Number.isFinite(idx)) return idx;
    // 3) NodeList の順序で算出
    const groups = [...wrapper.querySelectorAll(".c-variantPicker__options")];
    idx = groups.indexOf(group);
    return idx; // -1 の可能性は呼び出し側で弾く
  };

  const findVariantByOptions = (variants, selectedOptions) =>
    variants.find(v => arraysEqualNorm(v.options, selectedOptions));

  const computeAvailabilityMap = (variants, selected, changingIndex) => {
    const map = new Map();
    const values = new Set(variants.map(v => v.options[changingIndex]));
    values.forEach(val => {
      const trial = selected.slice();
      trial[changingIndex] = val;
      const match = variants.find(v =>
        v.options.every((opt, idx) => norm(trial[idx]) === norm(opt)) && v.available
      );
      map.set(norm(val), Boolean(match));
    });
    return map;
  };

  const updateUi = (wrapper, variants, selected) => {
    const variant =
      findVariantByOptions(variants, selected) ||
      variants.find(v => arraysEqualNorm(v.options, selected));

    // --- find form & hidden id
    let form = wrapper.closest("form");
    if (!form) form = document.querySelector('form[action*="/cart/add"]');
    const idInput = form?.querySelector('input[name="id"]');

    if (!idInput) {
      console.warn('[variant-picker] hidden input[name="id"] が見つかりません。テーマ側のマークアップを確認してください。');
    } else if (variant) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // price
    const priceEl = wrapper.querySelector("[data-price-target]");
    if (priceEl && variant) {
      const regular = (variant.compare_at_price && variant.compare_at_price > variant.price) ? variant.compare_at_price : null;
      priceEl.innerHTML = regular
        ? `<span class="c-variantPicker__priceRegular">${formatMoney(regular)}</span><span class="c-variantPicker__priceSale">${formatMoney(variant.price)}</span>`
        : `<span class="c-variantPicker__priceNow">${formatMoney(variant.price)}</span>`;
    }

    // sku
    const skuEl = wrapper.querySelector("[data-sku-target]");
    if (skuEl) skuEl.textContent = (variant?.sku ? `SKU: ${variant.sku}` : "");

    // stock
    const stockEl = wrapper.querySelector("[data-stock-target]");
    if (stockEl) {
      stockEl.innerHTML = (variant && variant.available)
        ? `<span class="is-inStock">在庫あり</span>`
        : `<span class="is-outStock">在庫なし</span>`;
    }

    // add-to-cart button state
    if (form) {
      const addBtn = form.querySelector('[type="submit"], button[name="add"]');
      if (addBtn) {
        const ok = Boolean(variant && variant.available);
        addBtn.disabled = !ok;
        addBtn.classList.toggle("is-disabled", !ok);
        addBtn.setAttribute("aria-disabled", ok ? "false" : "true");
      }
    }

    // disable impossible combos + visual state
    wrapper.querySelectorAll(".c-variantPicker__options").forEach(group => {
      const idx = Number.parseInt(group.dataset.optionIndex, 10);
      const availability = computeAvailabilityMap(variants, selected, idx);
      group.querySelectorAll(".c-variantPicker__option").forEach(btn => {
        const val = norm(btn.dataset.value);
        const selectable = availability.get(val);
        if (selectable) {
          btn.classList.remove("is-disabled");
          btn.removeAttribute("disabled");
          btn.setAttribute("aria-disabled", "false");
        } else {
          btn.classList.add("is-disabled");
          btn.setAttribute("disabled", "disabled");
          btn.setAttribute("aria-disabled", "true");
        }
        const isSel = norm(selected[idx]) === val;
        btn.classList.toggle("is-selected", isSel);
        btn.setAttribute("aria-checked", isSel ? "true" : "false");
      });
    });
  };

  const initPicker = (wrapper) => {
    if (!wrapper || wrapper.dataset.vpInitialized === "1") return;
    wrapper.dataset.vpInitialized = "1"; // mark

    const variants    = parseJSON(wrapper.dataset.variants || "[]", []);
    const optionsMeta = parseJSON(wrapper.dataset.options   || "[]", []);
    let   selected    = parseJSON(wrapper.dataset.selected  || "[]", []);

    if (!Array.isArray(selected) || selected.length !== optionsMeta.length) {
      selected = optionsMeta.map(o => norm(o.values?.[0]));
    } else {
      selected = selected.map(norm);
    }

    // 通常ルート（ラッパー内クリック）
    wrapper.addEventListener("click", (e) => {
      const btn = e.target.closest(".c-variantPicker__option");
      if (!btn || btn.classList.contains("is-disabled")) return;

      let idx = getOptionIndex(btn, wrapper);
      if (!Number.isFinite(idx) || idx < 0) return;

      const val = norm(btn.dataset.value);
      selected[idx] = val;
      try { wrapper.dataset.selected = JSON.stringify(selected); } catch(_) {}
      updateUi(wrapper, variants, selected);
    });

    updateUi(wrapper, variants, selected);
  };

  const initAll = () => {
    document.querySelectorAll(PICKER_SELECTOR).forEach(initPicker);
  };

  // first paint
  document.addEventListener("DOMContentLoaded", initAll);
  window.addEventListener("pageshow", initAll);
  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:select", initAll);

  // later DOM injections (app blocks / ajax)
  const mo = new MutationObserver(() => {
    if (document.querySelector(PICKER_SELECTOR + ":not([data-vp-initialized='1'])")) initAll();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // --- Safety net: delegated click (always-on, inside IIFE) ---
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".c-variantPicker__option");
    if (!btn) return;

    const wrapper = btn.closest(".c-variantPicker");
    if (!wrapper) return;

    const variants    = parseJSON(wrapper.dataset.variants || "[]", []);
    const optionsMeta = parseJSON(wrapper.dataset.options   || "[]", []);
    let   selected    = parseJSON(wrapper.dataset.selected  || "[]", []);
    if (!Array.isArray(selected) || selected.length !== optionsMeta.length) {
      selected = optionsMeta.map(o => norm(o.values?.[0]));
    } else {
      selected = selected.map(norm);
    }

    let idx = getOptionIndex(btn, wrapper);
    if (!Number.isFinite(idx) || idx < 0) return;

    const val = norm(btn.dataset.value);
    selected[idx] = val;
    try { wrapper.dataset.selected = JSON.stringify(selected); } catch(_) {}

    try { updateUi(wrapper, variants, selected); }
    catch (err) { console.error("[variant-picker] updateUi error:", err); }

    // 視覚状態を即保証
    wrapper.querySelectorAll(".c-variantPicker__options").forEach(group => {
      const gi = Number.parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll(".c-variantPicker__option").forEach(b => {
        const v = norm(b.dataset.value);
        const isSel = gi === idx ? (v === val) : (norm(selected[gi]) === v);
        b.classList.toggle("is-selected", isSel);
        b.setAttribute("aria-checked", isSel ? "true" : "false");
      });
    });
  }, true); // capture=true で取りこぼし防止
})();
